import { BookingWizardPageParams } from 'Views/Pages/BookingWizardPage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
	BookingWizardData,
	BookingWizardInnerDataType,
	copyWizardDataWithOwnTickets,
} from './BookingWizardData/BaseBookingWizardData';
import { getInitialBulkBookingFilters } from 'Modules/BulkBooking/Utils';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { clearFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { Journey } from 'Global/Journey/Types/Journey';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { getDefaultVehicleWizardData } from './BookingWizardData/VehicleBookingWizardData';
import { getDefaultFreightWizardData } from './BookingWizardData/FreightBookingWizardData';
import { getDefaultPassengerWizardData } from './BookingWizardData/PassengerBookingWizardData';
import locationAndRoutesStore from 'Models/LocationAndRoutesStore';
import { store } from 'Models/Store';
import alertToast from 'Util/ToastifyUtils';
import { TabSelected, TripType, WizardErrors } from './BookingWizardTypes';
import { devLog } from 'Util/_HumanWritten/ConsoleLogUtils';
import { createWizardStepsRecord } from 'Global/WizardSteps/wizardStepUtils';
import { TermsAndConditionsType } from './WizardSteps/TermsAndConditions/TermsAndConditions';
import { useParkingWizardStore } from 'Stores/ParkingWizardStore';
import { canAccessBulkBookings } from 'Modules/BulkBooking/Permissions';

export const WIZARD_DATA_LOCAL_STORAGE_KEY = 'ferry-booking-wizard-data-key';

export const sessionStorageEngine = {
	getItem: (name: string) => {
		const value = sessionStorage.getItem(name);
		try {
			return value ? JSON.parse(value) : null;
		} catch (error) {
			console.error('Failed to parse stored value:', error);
			return null;
		}
	},
	setItem: (name: string, value: string) => {
		sessionStorage.setItem(name, value);
	},
	removeItem: (name: string) => {
		sessionStorage.removeItem(name);
	},
};

export interface UseBookingWizardDataStoreProps {
	wizardData: BookingWizardData;
	oldWizardData: BookingWizardData | null;
	baseWizardErrors: WizardErrors<BookingWizardData>;
	innerWizardErrors: WizardErrors<BookingWizardInnerDataType>;
	transactionId?: string;
	termsAndConditionsTypes?: TermsAndConditionsType[];
	setWizardData(data: BookingWizardData): void;
	clearWizardData(): void;
	setOldWizardData(data: BookingWizardData): void;
	clearOldWizardData(): void;
	setTransactionId(id: string): void;
	setBaseWizardErrors(errors: WizardErrors<BookingWizardData>): void;
	setInnerWizardErrors(errors: WizardErrors<BookingWizardInnerDataType>): void;
	setTermsAndConditionsTypes(data: TermsAndConditionsType[]): void;
}

export const getWizardData = () => {
	if (typeof WIZARD_DATA_LOCAL_STORAGE_KEY === 'undefined') {
		console.warn('WIZARD_DATA_LOCAL_STORAGE_KEY is not defined yet.');
		return new BookingWizardData(getDefaultWizardData());
	}
	const wizardData = sessionStorage.getItem(WIZARD_DATA_LOCAL_STORAGE_KEY);
	if (isNotNullOrUndefined(wizardData)) {
		const parsedData = JSON.parse(wizardData);

		// Fallback for old wizard data
		if (parsedData && 'adultTickets' in parsedData) {
			alertToast('Error', 'error', 'The booking wizard has been updated. You will need to start over.');
			return new BookingWizardData(parsedData);
		}
		return new BookingWizardData(parsedData.state.wizardData);
	}
	return new BookingWizardData(getDefaultWizardData());
};

export const setWizardData = (wizardData: BookingWizardData) => {
	useBookingWizardDataStore.getState().setWizardData(wizardData);
};

export const useBookingWizardDataStore = create<UseBookingWizardDataStoreProps, [['zustand/persist', unknown]]>(
	persist(
		(set, get) => ({
			wizardData: getBookingWizardData(null),
			oldWizardData: getOldWizardData(),
			baseWizardErrors: {},
			innerWizardErrors: {},
			transactionId: '',
			termsAndConditionsTypes: [],
			setWizardData(data: BookingWizardData) {
				set(() => ({ wizardData: new BookingWizardData(data) }));
			},
			clearWizardData() {
				set(() => ({ wizardData: getDefaultWizardData() }));
			},
			setOldWizardData(data: BookingWizardData) {
				// Deep copy, deliberately: `new BookingWizardData(data)` alone shares the passenger
				// ticket arrays with the live wizard data, so "old" moved whenever "new" did and
				// passenger changes compared equal. See `copyWizardDataWithOwnTickets`.
				set(() => ({ oldWizardData: copyWizardDataWithOwnTickets(data) }));
			},
			clearOldWizardData() {
				set(() => ({ oldWizardData: null }));
			},
			setTransactionId(id: string) {
				set(() => ({ transactionId: id }));
			},
			setBaseWizardErrors(errors: WizardErrors<BookingWizardData>) {
				set(() => ({ baseWizardErrors: errors }));
			},
			setInnerWizardErrors(errors: WizardErrors<BookingWizardInnerDataType>) {
				set(() => ({ innerWizardErrors: errors }));
			},
			setTermsAndConditionsTypes(data: TermsAndConditionsType[]) {
				set(() => ({ termsAndConditionsTypes: data }));
			},
		}),
		{
			name: WIZARD_DATA_LOCAL_STORAGE_KEY,
			storage: createJSONStorage(() => sessionStorageEngine),
		},
	),
);

export function clearBookingWizardData() {
	// clear in-memory store state
	useBookingWizardDataStore.setState({
		wizardData: getDefaultWizardData(),
		oldWizardData: null,
		baseWizardErrors: {},
		innerWizardErrors: {},
		transactionId: '',
		termsAndConditionsTypes: [],
	});

	// clear parking wizard state
	useParkingWizardStore.getState().reset();

	// clear storage
	sessionStorageEngine.removeItem(WIZARD_DATA_LOCAL_STORAGE_KEY);

	devLog('DEV0015: Clearing booking wizard data');
}

export function getOldWizardData() {
	if (typeof WIZARD_DATA_LOCAL_STORAGE_KEY === 'undefined') {
		console.warn('WIZARD_DATA_LOCAL_STORAGE_KEY is not defined yet.');
		return null;
	}
	const oldWizardData = sessionStorage.getItem(WIZARD_DATA_LOCAL_STORAGE_KEY);
	if (isNotNullOrUndefined(oldWizardData)) {
		const parsedData = JSON.parse(oldWizardData);

		// Fallback for old wizard data
		if (parsedData.state.oldWizardData === null) {
			return null;
		}
		return new BookingWizardData(parsedData.state.oldWizardData);
	}
	return null;
}

export function clearOldWizardData() {
	// Get the stored data (already parsed as an object)
	useBookingWizardDataStore.getState().clearOldWizardData();
	devLog('DEV0016: Clearing OLD booking wizard data');
}

export function getBookingWizardData(params: BookingWizardPageParams | null): BookingWizardData {
	try {
		const fetchedData = getWizardData();

		// Use the fetched data if it exists and there are no params
		if (fetchedData !== null && params === null) {
			return fetchedData;
		}

		// Otherwise create new wizard data
		const oldWizardData = getOldWizardData();
		const newData = getDefaultWizardData();

		// If there are any params passed in we want to use them
		if (!!params) {
			switch (params.type ?? newData.tabSelected) {
				case 'vehicle':
					newData.tabSelected = 'vehicle';
					newData.tripType = whiteLabelStore.GetVehicleDefaultTripType;
					newData.innerWizardData = getDefaultVehicleWizardData();
					break;
				case 'freight':
					newData.tabSelected = 'freight';
					newData.tripType = 'one way';
					newData.innerWizardData = getDefaultFreightWizardData();
					break;
				default:
					newData.tabSelected = 'passenger';
					newData.tripType = whiteLabelStore.GetPassengerDefaultTripType;
					newData.innerWizardData = getDefaultPassengerWizardData();
			}

			// This will override any fields which were supplied in the search params.
			// By having the fallback value be the original value of the property we are able to ensure that we
			// aren't setting potentially conflicting default values
			newData.tabSelected = params.type ?? newData.tabSelected;
			newData.startDate = params.date ?? newData.startDate;
			newData.ticketSelectionStartDate = params.date ?? newData.startDate;
			newData.endDate = params.date ?? newData.startDate;
			newData.ticketSelectionEndDate = params.date ?? newData.startDate;
			newData.fromLocationId = params.fromLocation ?? newData.fromLocationId;
			newData.toLocationId = params.toLocation ?? newData.toLocationId;
			newData.departureTicketId = params.ferryTripId ?? newData.departureTicketId;
			newData.bookingToEdit = params.bookingId ?? '';

			// Set the user Id from the old booking wizard data if there is any
			newData.userId = oldWizardData?.userId ?? '';
			if (params.bookingId) {
				newData.wizardMode = 'ALTERATION';
				newData.tripType = 'one way';
				newData.restrictedMode = false;
			}

			// Feature: Multi-stop - assign from wizard page params
			// Only assign departing journey when all required params are present
			const departingJourney = getDepartingJourney(params);
			if (departingJourney) {
				newData.departingJourney = departingJourney;
			}

			// Only returning journey when all required params are present
			const returningJourney = getReturningJourney(params);
			if (returningJourney) {
				newData.returningJourney = returningJourney;
			}

			if (oldWizardData !== null) {
				clearOldWizardData();
			}

			// Handle Bulk Booking Mode
			const isBulkMode = params.mode === 'bulk';

			if (isBulkMode && canAccessBulkBookings()) {
				// Initialize bulk booking filters
				newData.bulkBookingFilters = getInitialBulkBookingFilters();
				// Ensure we are in CREATE mode for bulk, not alteration
				newData.wizardMode = 'CREATE';
				// Reset any alteration-specific fields that might have been set above
				newData.bookingToEdit = '';
				newData.departureTicketId = '';
				newData.returningTicketId = '';
			}

			// Never carry a transaction id across a flow switch. An abandoned alteration leaves its
			// transaction open in storage; adopting it for a new booking means paying for the new
			// booking also commits the pending edit. Checking `fetchedData` as well as `oldWizardData`
			// matters — an edit the user walked away from lives in the CURRENT wizard data, and
			// `oldWizardData` is only populated on some routes into the wizard.
			// Starting an alteration is already covered: onEdit clears storage before redirecting.
			const leavingAnAlteration =
				oldWizardData?.wizardMode === 'ALTERATION' || fetchedData?.wizardMode === 'ALTERATION';

			if (leavingAnAlteration && newData.wizardMode !== 'ALTERATION') {
				clearFerryBookingTransactionIdFromStorage();
			}
		}

		setWizardData(newData);
		return newData;
	} catch (error) {
		// If anything goes wrong, clear the booking wizard data and start over... this should never happen
		// but we want to be safe.
		console.error('Error getting booking wizard data', error);
		clearBookingWizardData();
		const newData = new BookingWizardData(getDefaultWizardData());
		setWizardData(newData);
		return newData;
	}
}

interface DefaultWizardDataOptions {
	tabSelected?: TabSelected;
	userId?: string;
}

export function getDefaultWizardData(options: DefaultWizardDataOptions = {}): BookingWizardData {
	const defaultRoute = locationAndRoutesStore.getDefaultRoute() ?? locationAndRoutesStore.getAnyRoute();

	const defaultTabSelected = whiteLabelStore.defaultBookingType;
	const tabSelected = options.tabSelected ?? defaultTabSelected;

	let defaultTripType: TripType =
		whiteLabelStore.defaultBookingType === 'vehicle'
			? whiteLabelStore.GetVehicleDefaultTripType
			: whiteLabelStore.GetPassengerDefaultTripType;

	let innerWizardData: BookingWizardInnerDataType = getDefaultPassengerWizardData();
	if (tabSelected === 'vehicle') {
		innerWizardData = getDefaultVehicleWizardData();
	}
	if (tabSelected === 'freight') {
		innerWizardData = getDefaultFreightWizardData();
		defaultTripType = 'one way';
	}

	return new BookingWizardData({
		wizardMode: 'CREATE',
		restrictedMode: false,
		acceptedTsAndCs: false,
		acceptedTsAndCsMap: createWizardStepsRecord(),
		startDate: new Date(),
		endDate: new Date(),
		tabSelected,
		tripType: defaultTripType,
		fromLocationId: defaultRoute?.departureId ?? '',
		toLocationId: defaultRoute?.destinationId ?? '',
		bookingToEdit: '',
		departureTrip: undefined,
		departureTicketId: '',
		returningTicketId: '',
		userId: options.userId ?? '',
		ticketSelectionStartDate: new Date(),
		ticketSelectionEndDate: new Date(),
		departingCancellationFee: 0,
		returningCancellationFee: 0,
		departingCancellationCutoffHours: 0,
		associatedTripDateTime: undefined,
		automaticallyApplyAddOnsToBothDirections: true,
		disabledVehicleType: false,
		userSelectedTripType: false,
		innerWizardData,
	});
}

function getDepartingJourney(params: BookingWizardPageParams): Journey | null {
	const { departingTripId, departingStartStopId, departingEndStopId } = params;

	if (departingTripId && departingStartStopId && departingEndStopId) {
		const journey: Journey = {
			tripId: departingTripId,
			startStopId: departingStartStopId,
			endStopId: departingEndStopId,
		};
		devLog('WIZARD: Successfully selected a departing trip from booking wizard page parameters', journey);
		return journey;
	}

	if (process.env.NODE_ENV === 'development') {
		console.warn('WIZARD: Attempted to define a departing trip, but had missing info.', {
			departingTripId,
			departingStartStopId,
			departingEndStopId,
		});
	}

	return null;
}

function getReturningJourney(params: BookingWizardPageParams): Journey | null {
	const { returningTripId, returningStartStopId, returningEndStopId } = params;

	if (returningTripId && returningStartStopId && returningEndStopId) {
		const journey: Journey = {
			tripId: returningTripId,
			startStopId: returningStartStopId,
			endStopId: returningEndStopId,
		};
		devLog('WIZARD: Successfully selected a returning trip from booking wizard page parameters', journey);
		return journey;
	}

	if (process.env.NODE_ENV === 'development') {
		console.warn('WIZARD: Attempted to define a returning trip, but had missing info.', {
			returningTripId,
			returningStartStopId,
			returningEndStopId,
		});
	}

	return null;
}

export function getOldFerryBookingWizardData(): BookingWizardData | null {
	// const fetchedData = sessionStorage.getItem(OLD_WIZARD_DATA_LOCAL_STORAGE_KEY);
	//
	// return fetchedData === null ? null : JSON.parse(fetchedData);
	return null;
}
